import React, {useEffect, useState} from "react";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  MenuItem,
  Select,
  Menu,
  Divider,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {useTenantTheme} from "../../themes/TenantThemeProvider";
import {tenantThemes} from "../../themes/tenantThemes";
import LanguageSelector from "./LanguageSelector";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import {fetchTenant, getTenantLogoUrl} from "../../api/tenant";

interface AppBarProps {
  handleDrawerToggle: () => void;
  onNotificationClick: () => void;
  unreadCount?: number;
}

const AppBar: React.FC<AppBarProps> = ({handleDrawerToggle, onNotificationClick, unreadCount = 0}) => {
  const {t} = useTranslation();
  const {tenant, setTenant, themeConfig, tenantLogo} = useTenantTheme();
  const {user, logout} = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenantLogoUrl, setTenantLogoUrl] = useState<string | null>(null);
  const [tenantDisplayName, setTenantDisplayName] = useState<string>("SmartFarm");
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // App logo should always use the static public asset.
  const appLogoUrl = `${process.env.PUBLIC_URL || ""}/logo.png`;
  const effectiveTenantLogoUrl = tenantLogoUrl || tenantLogo || null;

  useEffect(() => {
    const loadTenantBranding = async () => {
      if (!user?.tenantId) {
        setTenantLogoUrl(null);
        setTenantDisplayName("SmartFarm");
        return;
      }

      try {
        const [tenantData, logoResponse] = await Promise.all([fetchTenant(user.tenantId), getTenantLogoUrl(user.tenantId).catch(() => ({url: ""}))]);

        setTenantDisplayName(tenantData.name || "SmartFarm");
        if (tenantData.theme) {
          setTenant(tenantData.theme);
        }
        setTenantLogoUrl(logoResponse.url || null);
      } catch (error) {
        setTenantDisplayName("SmartFarm");
        setTenantLogoUrl(null);
      }
    };

    void loadTenantBranding();
  }, [user?.tenantId, setTenant]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <MuiAppBar position="static" color="default" elevation={1} sx={{zIndex: 1200, minWidth: 0}}>
      <Toolbar sx={{py: 1, minWidth: 0, gap: 1}}>
        {isMobile && (
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{mr: 2}}>
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            overflow: "hidden",
          }}>
          <img src={appLogoUrl} alt="App Logo" style={{height: "44px", verticalAlign: "middle", borderRadius: 8, flexShrink: 0}} />
          {effectiveTenantLogoUrl ? (
            <img
              src={effectiveTenantLogoUrl}
              alt={`${tenantDisplayName} Logo`}
              style={{
                height: "24px",
                width: "24px",
                borderRadius: "25%",
                alignSelf: "flex-start",
                marginTop: 2,
                flexShrink: 0,
              }}
            />
          ) : (
            ""
          )}

          <Box sx={{minWidth: 0, overflow: "hidden"}}>
            <Typography variant="h6" noWrap sx={{fontWeight: 700, color: "text.primary", ml: 0.25}} title={tenantDisplayName}>
              {tenantDisplayName}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{color: "text.secondary", ml: 0.15, display: "block", lineHeight: 1, mt: 0.2}}>
              -- SmartFarm --
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
          {/* Language Selector */}
          <Box sx={{display: {xs: "none", md: "flex"}}}>
            <LanguageSelector />
          </Box>
          {/* Notification Bell */}
          <IconButton color="inherit" onClick={onNotificationClick}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleMenu}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{horizontal: "right", vertical: "top"}}
            anchorOrigin={{horizontal: "right", vertical: "bottom"}}>
            {user && (
              <Box px={2} py={1}>
                <Typography variant="subtitle2">{user.name || user.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Theme: {themeConfig.name}
                </Typography>
              </Box>
            )}
            <Divider />
            <Box px={2} py={1}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Select Theme
              </Typography>
              <Select size="small" fullWidth value={tenant} onChange={(e) => setTenant(e.target.value)}>
                {Object.entries(tenantThemes).map(([themeName, theme]) => (
                  <MenuItem key={themeName} value={themeName}>
                    {theme.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t("Logout")}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
